import React, { useEffect, useMemo, useRef, useState } from "react";
import { companyProfileService } from "../../../_services/companyProfile.service";
import PhoneNumberInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
    T, StatusBadge, Avatar, StatCard, SectionHeading,
    InfoRow, FieldInput, Banner, ActionsPanel, STAT_DEFS,
    FiHome, FiHash, FiDollarSign, FiMapPin, FiMail,
    FiPhone, FiEdit3, FiCheckCircle, FiBookOpen, FiUser
} from "./CompanyParts";

/* ── Constants for validation and UI ── */
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = new Set(["image/svg+xml", "image/png", "image/jpeg", "image/webp", "image/avif"]);

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,3}$/.test((email || "").trim());

function stripPhone(raw) {
    return raw.replace(/[\s\-().]/g, "");
}

function normalizePhone(raw) {
    if (!raw) return null;
    const stripped = stripPhone(raw);

    let local = stripped;
    if (local.startsWith("+60")) local = local.slice(3);
    else if (local.startsWith("60")) local = local.slice(2);
    else if (local.startsWith("0")) local = local.slice(1);

    if (!/^\d{7,10}$/.test(local)) return null;

    if (local.startsWith("1") && local.length >= 9) {
        const prefix = local.slice(0, 2);
        const mid = local.slice(2, 5);
        const last = local.slice(5);
        return `+60 ${prefix}-${mid} ${last}`;
    }

    if (local.length >= 7) {
        const areaCode = local.slice(0, local.length - 7 + 1);
        const number = local.slice(areaCode.length);
        const firstHalf = number.slice(0, 4);
        const secondHalf = number.slice(4);
        if (secondHalf) {
            return `+60 ${areaCode}-${firstHalf} ${secondHalf}`;
        }
        return `+60 ${areaCode}-${firstHalf}`;
    }

    return null;
}

const isValidPhone = (phone) => normalizePhone(phone) !== null;

const AUTOCLOSE = { success: 3000, info: 2500, danger: 5000 };
const EMPTY_FORM = {
    CompanyName: "", RegNo: "", TaxNo: "",
    Address: "", PhoneNumber: "", Email: "",
    Status: "Inactive", LogoUrl: "",
};

const hasChanges = (a, b) => Object.keys(a).some((k) => a[k] !== b[k]);

/* ── FIX: Company status gate banner ──────────────────────────────────────
   Shown when the company profile is incomplete or status is Inactive.
   Blocks users from creating Sales / Purchase documents until resolved.     */
function CompanyStatusGateBanner({ isFirstTime, isInactive }) {
    if (!isFirstTime && !isInactive) return null;

    const msg = isFirstTime
        ? "Company profile not set up. Please complete your company profile before creating Sales or Purchase documents."
        : "Your company status is Inactive. Set status to Active to create Sales or Purchase documents.";

    return (
        <div style={{
            padding: "12px 18px",
            borderRadius: "8px",
            marginBottom: 16,
            fontSize: 14,
            fontWeight: 500,
            background: "#fffbeb",
            color: "#92400e",
            border: "1px solid #fcd34d",
            display: "flex",
            alignItems: "center",
            gap: 10,
        }}>
            <span>{msg}</span>
        </div>
    );
}

export default function Company() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [original, setOriginal] = useState(EMPTY_FORM);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    /* FIX: purchaseOrders replaces bills in metrics */
    const [metrics, setMetrics] = useState({ customers: 0, suppliers: 0, invoices: 0, purchaseOrders: 0 });
    const [logoFile, setLogoFile] = useState(null);
    const fileInputRef = useRef(null);
    const bannerTimerRef = useRef(null);

    const canSave = useMemo(() => {
        const { CompanyName, RegNo, TaxNo, Address, PhoneNumber, Email, Status } = form;
        const basicFields = !!(CompanyName && RegNo && TaxNo && Address && PhoneNumber && Email && Status);
        const validFormats = isValidEmail(Email) && isValidPhone(PhoneNumber);
        return basicFields && validFormats && hasChanges(form, original);
    }, [form, original]);

    /* Auto-hide banner */
    useEffect(() => {
        if (!banner.msg) return;
        clearTimeout(bannerTimerRef.current);
        bannerTimerRef.current = setTimeout(
            () => setBanner({ type: "", msg: "" }),
            AUTOCLOSE[banner.type] ?? 3000
        );
        return () => clearTimeout(bannerTimerRef.current);
    }, [banner]);

    /* Load Profile and Metrics */
    useEffect(() => {
        let alive = true;
        const load = async () => {
            setLoading(true);
            try {
                const [profile, stats] = await Promise.all([
                    companyProfileService.getProfile(),
                    companyProfileService.getMetrics()
                ]);
                if (!alive) return;
                setForm(profile);
                setOriginal(profile);
                setMetrics(stats);

                /* FIX: First-time setup prompt */
                if (!profile.CompanyName) {
                    setBanner({
                        type: "info",
                        msg: "Welcome! No profile found for this account. Click 'Edit Profile' to get started."
                    });
                }
            } catch (err) {
                if (alive) setBanner({ type: "danger", msg: "Failed to load company data." });
            } finally {
                if (alive) setLoading(false);
            }
        };
        load();
        return () => { alive = false; };
    }, []);

    /* Handlers */
    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onEdit = () => setIsEditing(true);

    const resetForm = (msg) => {
        setForm(original);
        setIsEditing(false);
        setLogoFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (msg) setBanner({ type: "info", msg });
    };

    const onSave = async () => {
        if (!isValidEmail(form.Email)) {
            setBanner({ type: "danger", msg: "Please enter a valid email address (e.g. user@example.com)." });
            return;
        }

        if (!form.PhoneNumber || !isValidPhoneNumber(form.PhoneNumber)) {
            setBanner({ type: "danger", msg: "Please enter a valid phone number in international format (e.g. +60 12-345 6789)." });
            return;
        }

        if (!hasChanges(form, original)) {
            setBanner({ type: "info", msg: "No changes detected." });
            return;
        }

        const formToSave = { ...form };

        setSaving(true);
        setBanner({ type: "", msg: "" });
        try {
            const updatedProfile = await companyProfileService.updateProfile(formToSave, logoFile);
            setOriginal(updatedProfile);
            setForm(updatedProfile);
            setIsEditing(false);
            setLogoFile(null);
            setBanner({ type: "success", msg: "Company profile saved successfully." });
        } catch (err) {
            setBanner({ type: "danger", msg: "Failed to save. Please try again." });
        } finally {
            setSaving(false);
        }
    };

    /* FIX: isFirstTime = profile has never been saved (no company name) */
    const isFirstTime = !original.CompanyName;
    /* FIX: isInactive = profile exists but status is Inactive */
    const isInactive = !isFirstTime && original.Status !== "Active";

    const onSelectLogo = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > MAX_BYTES) {
            setBanner({ type: "danger", msg: "File too large. Max 5 MB." });
            e.target.value = "";
            return;
        }

        if (!ALLOWED_MIME.has(file.type)) {
            setBanner({ type: "danger", msg: "Unsupported format (SVG, PNG, JPG, WEBP, AVIF only)." });
            e.target.value = "";
            return;
        }

        const reader = new FileReader();
        reader.onload = (ev) => {
            setForm((f) => ({ ...f, LogoUrl: ev.target.result }));
            setLogoFile(file);
        };
        reader.readAsDataURL(file);
    };

    const onRemoveLogo = () => {
        setForm((f) => ({ ...f, LogoUrl: "" }));
        setLogoFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    return (
        <div style={{ padding: "20px 0", fontSize: T.fontSizeBase }}>
            {/* Header */}
            <div style={{ marginBottom: 18 }}>
                <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0 }}>Company Profile</h4>
                <p style={{ fontSize: T.fontSizeXs, color: T.textMuted, margin: "3px 0 0" }}>
                    Manage your company profile details.
                </p>
            </div>

            <Banner type={banner.type} msg={banner.msg} onClose={() => setBanner({ type: "", msg: "" })} />

            {/* FIX: Status gate banner — visible whenever profile is incomplete or inactive */}
            {!loading && (
                <CompanyStatusGateBanner isFirstTime={isFirstTime} isInactive={isInactive} />
            )}

            {/* Metrics */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 12,
                marginBottom: 20,
            }}>
                {STAT_DEFS.map((s) => (
                    <StatCard key={s.label} icon={s.icon} color={s.color} label={s.label} value={metrics[s.key]} />
                ))}
            </div>

            {/* Profile Card */}
            <div style={{
                background: T.cardBg, borderRadius: T.radius,
                border: `1.5px solid ${T.border}`, boxShadow: T.shadow,
                overflow: "hidden",
            }}>

                <input ref={fileInputRef} type="file" multiple={false}
                    accept="image/*"
                    onChange={onSelectLogo} style={{ display: "none" }} />

                {/* Profile Title Section */}
                <div style={{
                    display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap",
                    padding: "20px 24px", borderBottom: `1px solid ${T.border}`,
                }}>
                    <Avatar logoUrl={form.LogoUrl} name={form.CompanyName} size={60} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: T.fontSizeXs, fontWeight: 700, color: T.textLight, letterSpacing: "0.08em", marginBottom: 3 }}>
                            COMPANY PROFILE
                        </div>
                        <div style={{ fontSize: T.fontSizeLg, fontWeight: 700, color: T.textPrimary, lineHeight: 1.25, marginBottom: 6 }}>
                            {form.CompanyName || "Company Name"}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: T.fontSizeXs, color: T.textMuted }}>
                            <span>Reg No: <strong style={{ color: T.textPrimary }}>{form.RegNo || "—"}</strong></span>
                            <span style={{ color: T.border }}>|</span>
                            <span>Tax No: <strong style={{ color: T.textPrimary }}>{form.TaxNo || "—"}</strong></span>
                            <span style={{ color: T.border }}>|</span>
                            <StatusBadge value={isFirstTime ? "Inactive" : form.Status} />
                        </div>
                    </div>

                    <div style={{ flexShrink: 0 }}>
                        {!isEditing && (
                            <button onClick={onEdit} style={{
                                display: "flex", alignItems: "center", gap: 7,
                                padding: "8px 16px", borderRadius: T.radiusSm,
                                background: T.accent, color: "#fff",
                                border: "none", cursor: "pointer",
                                fontSize: T.fontSizeSm, fontWeight: 600,
                                boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
                            }}>
                                <FiEdit3 size={13} /> Edit Profile
                            </button>
                        )}
                    </div>
                </div>

                {/* Body Content */}
                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "48px 0", color: T.textMuted, fontSize: T.fontSizeBase }}>
                        <span>Loading profile…</span>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 400px", padding: "22px 24px", minWidth: 0 }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0 36px" }}>
                                {/* Left Column: General */}
                                <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                                    <SectionHeading icon={FiBookOpen} title="General" />
                                    {isEditing ? (
                                        <>
                                            <FieldInput label="Company Name" name="CompanyName" value={form.CompanyName} onChange={onChange} required placeholder="Enter your company name" />
                                            <div style={{ display: "flex", gap: 12 }}>
                                                <FieldInput label="Reg No" name="RegNo" value={form.RegNo} onChange={onChange} required placeholder="xxxx01xxxxxx" />
                                                <FieldInput label="Tax No" name="TaxNo" value={form.TaxNo} onChange={onChange} required placeholder="Cxxxxxxxxx0" />
                                            </div>
                                            <FieldInput label="Address" name="Address" as="textarea" rows={3} value={form.Address} onChange={onChange} required placeholder="Street, City, Postcode, Country" />
                                        </>
                                    ) : (
                                        <>
                                            <InfoRow icon={FiHome} label="Company Name">{form.CompanyName}</InfoRow>
                                            <InfoRow icon={FiHash} label="Reg No.">{form.RegNo}</InfoRow>
                                            <InfoRow icon={FiDollarSign} label="Tax No.">{form.TaxNo}</InfoRow>
                                            <InfoRow icon={FiMapPin} label="Address">{form.Address}</InfoRow>
                                        </>
                                    )}
                                </div>

                                {/* Right Column: Contact */}
                                <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                                    <SectionHeading icon={FiUser} title="Contact" />
                                    {isEditing ? (
                                        <>
                                            <FieldInput label="Email" name="Email" type="email" value={form.Email} onChange={onChange} required placeholder="example@company.com" />
                                            {/* NEW: library-based phone input */}
                                            <div style={{ marginBottom: 14 }}>
                                                <label style={{
                                                    display: "block",
                                                    fontSize: T.fontSizeXs,
                                                    fontWeight: 600,
                                                    color: T.textMuted,
                                                    marginBottom: 5,
                                                }}>
                                                    Phone Number <span style={{ color: T.danger }}>*</span>
                                                </label>
                                                <PhoneNumberInput
                                                    international
                                                    defaultCountry="MY"              // or tie to a stored country if you add one
                                                    countryCallingCodeEditable={false}
                                                    value={form.PhoneNumber || ""}
                                                    onChange={(val) => setForm(f => ({ ...f, PhoneNumber: val || "" }))}
                                                    placeholder="+60 12-3456 789"
                                                    style={{
                                                        width: "100%",
                                                        height: 36,
                                                        padding: "0 11px",
                                                        fontSize: T.fontSizeBase,
                                                        color: T.textPrimary,
                                                        background: "#fff",
                                                        fontFamily: "inherit",
                                                        border: `1.5px solid ${T.border}`,
                                                        borderRadius: T.radiusSm,
                                                        outline: "none",
                                                        boxSizing: "border-box",
                                                    }}
                                                />
                                            </div>
                                            <FieldInput label="Status" name="Status" as="select" value={form.Status} onChange={onChange}>
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                            </FieldInput>
                                        </>
                                    ) : (
                                        <>
                                            <InfoRow icon={FiMail} label="Email">{form.Email}</InfoRow>
                                            <InfoRow icon={FiPhone} label="Phone Number">{form.PhoneNumber}</InfoRow>
                                            <InfoRow icon={FiCheckCircle} label="Status"><StatusBadge value={form.Status} /></InfoRow>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Actions Panel */}
                        {isEditing && (
                            <ActionsPanel
                                form={form}
                                onChange={onChange}
                                onUpload={() => fileInputRef.current?.click()}
                                onRemoveLogo={onRemoveLogo}
                                onSave={onSave}
                                onCancel={() => resetForm("Edit cancelled — no changes saved.")}
                                canSave={canSave}
                                saving={saving}
                                saveLabel={isFirstTime ? "Save" : "Save Changes"}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* FIX: First-time setup note at the bottom */}
            {!loading && isFirstTime && !isEditing && (
                <div style={{
                    marginTop: 16,
                    padding: "14px 18px",
                    borderRadius: "8px",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    fontSize: 13,
                    color: "#1e40af",
                    fontWeight: 500,
                }}>
                    👋 First time? Click <strong>Edit Profile</strong> above to set up your company. You must save a complete profile with <strong>Active</strong> status before you can create Sales or Purchase documents.
                </div>
            )}
        </div>
    );
}

/* ── Exported helper so other modules (Quotations, PO, etc.) can check
   if the company is ready before allowing document creation.
   Usage in any list/form page:
       import { isCompanyReady } from "../Company/Company";
       const ready = await isCompanyReady();
       if (!ready) { alert / navigate / disable button }            ── */
export async function isCompanyReady() {
    try {
        const profile = await companyProfileService.getProfile();
        return !!(profile.CompanyName && profile.Status === "Active");
    } catch {
        return false;
    }
}